import type { ActionReturn } from "svelte/action";

type ValidatableElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

const blurredInputs: ValidatableElement[] = [];

export function handleInvalidInputs(form: HTMLFormElement): ActionReturn {
	let invalidTimeout: number | undefined;
	let scrollTimeout: number | undefined;
	const registeredInputs: ValidatableElement[] = [];

	// https://stackoverflow.com/a/78227361
	function onInvalid(target: EventTarget | null): void {
		if (!(target instanceof HTMLElement)) {
			return;
		}

		const isCheckboxOrRadio =
			target instanceof HTMLInputElement && (target.type === "checkbox" || target.type === "radio");

		// Multiple elements in the form will likely report being invalid at the same time, so we wait 50ms
		// until they've stopped triggering
		clearTimeout(invalidTimeout);
		invalidTimeout = window.setTimeout(() => {
			// Inputs with custom validation UI (see Input.svelte) call preventDefault() on "invalid", which
			// stops the browser's own scroll-into-view/focus behavior, so we always do it ourselves here
			// instead of relying on the browser to have done it already.
			target.scrollIntoView({ behavior: "smooth", block: "center" });
			target.focus({ preventScroll: true });

			if (isCheckboxOrRadio) {
				// Calling form.reportValidity() again on a checkbox/radio can make WebKit (Safari) toggle its
				// checked state instead of just re-showing the validation UI, so we stop here for those.
				return;
			}

			// Wait until the scrolling triggered above has stopped before attempting to trigger the validation again
			let hasScrolled = false;
			function onScroll(): void {
				hasScrolled = true;
				clearTimeout(scrollTimeout);
				scrollTimeout = window.setTimeout(() => {
					// We've finished scrolling after an invalid event, so re-trigger the form validation
					form.reportValidity();
					window.removeEventListener("scroll", onScroll);
				}, 50);
			}

			window.addEventListener("scroll", onScroll);

			// No scroll happened, validate right away.
			setTimeout(() => {
				if (!hasScrolled) {
					clearTimeout(scrollTimeout);
					window.removeEventListener("scroll", onScroll);
				}
			}, 20);
		}, 50);
	}

	let ignore = true;
	const initialIgnoreMs = 500;
	setTimeout(() => {
		ignore = false;
	}, initialIgnoreMs);

	function listenToInvalidInputs(): void {
		if (ignore) {
			return;
		}

		for (const input of form.querySelectorAll<ValidatableElement>("input, textarea, select")) {
			if (registeredInputs.includes(input)) {
				continue;
			}

			input.addEventListener("invalid", ({ target }) => {
				onInvalid(target);
			});
			input.addEventListener("blur", () => {
				if (!blurredInputs.includes(input)) {
					blurredInputs.push(input);
				}
			});
			registeredInputs.push(input);

			if (input.validationMessage && blurredInputs.includes(input)) {
				onInvalid(input);
			}
		}
	}

	const observer = new MutationObserver((mutations) => {
		const isChildList = mutations.some((mutation) => mutation.type === "childList");
		if (!isChildList) {
			return;
		}
		listenToInvalidInputs();
	});
	observer.observe(form, { childList: true, subtree: true });

	return {
		destroy(): void {
			observer.disconnect();
		},
	};
}
